import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "./tokenStorage";

export function setupAuthInterceptors(axiosInstance, { refreshUrl, onLogout, publicUrls = [] }) {
  let refreshPromise = null;
  let loggedOut = false;
  function triggerLogout() {
    if (loggedOut) return;
    loggedOut = true;
    clearTokens();
    onLogout();
  }
  // Only a definite "this refresh token is no good" answer ends the session.
  // A network drop, timeout or 5xx (e.g. a 502 from the gateway) says nothing
  // about the token, so tokens are kept and the original request just fails;
  // the next request will try the refresh again.
  function isRefreshRejected(refreshError) {
    const status = refreshError && refreshError.response && refreshError.response.status;
    return status === 400 || status === 401 || status === 403;
  }
  function isPublicUrl(url) {
    return publicUrls.some((publicUrl) => url.includes(publicUrl));
  }

  axiosInstance.interceptors.request.use((config) => {
    if (config.url === refreshUrl || isPublicUrl(config.url)) {
      return config;
    }
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      if (!response || response.status !== 401 || !config) {
        return Promise.reject(error);
      }

      if (isPublicUrl(config.url)) {
        return Promise.reject(error);
      }

      if (config.url === refreshUrl) {
        triggerLogout();
        return Promise.reject(error);
      }

      if (config._retry) {
        return Promise.reject(error);
      }
      config._retry = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        triggerLogout();
        return Promise.reject(error);
      }

      if (!refreshPromise) {
        refreshPromise = axiosInstance
          .post(
            refreshUrl,
            { refresh_token: refreshToken },
            { headers: { "Request-ID": "allow|me", "Content-Type": "application/json" } }
          )
          .then((res) => {
            const tokens = res.data?.response ?? res.data;
            setTokens(tokens);
            return tokens.access_token;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      let newAccessToken;
      try {
        newAccessToken = await refreshPromise;
      } catch (refreshError) {
        if (isRefreshRejected(refreshError)) triggerLogout();
        return Promise.reject(refreshError);
      }

      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(config);
    }
  );
}
